<template>
    <div class="bulk-message-recipients-import-upload">
        <ContactImportUpload
            :override-import-type="'bulk-message-recipients'"
            :pageCopy="page_copy"
            :fieldDefinitions="field_definitions"
            :previewConfig="preview_config"
            :targetBulkMessageName="target_bulk_message_name"
            :targetBulkMessage="target_bulk_message"
            :importExecutionMode="import_execution_mode"
            @bulk-message-id-available="onBulkMessageIdAvailable"
        />
    </div>
</template>

<script>
import ContactImportUpload from '@/components/contacts/contact-import-component.vue';

export default {
    name: 'BulkMessageRecipientsImportUpload',
    components: { ContactImportUpload },
    data() {
        return {
            target_bulk_message: null,
            target_bulk_message_name: null,
            bulk_message_poll_interval: null,
            polling_bulk_message_id: null,
        };
    },
    mounted() {
        // For new imports, ensure bulk_message_id is present
        if (!this.$route.query.bulk_message_id && !this.$route.params.import_id)
        {
            this.$rc.alert('Bulk Message is required for bulk recipients import', 'error');
            this.$router.push(`/app/bulk/messages/${this.$route.params.organization_id}`);
            return;
        }
        // Load target bulk message when provided via query (new import)
        if (this.$route.query.bulk_message_id)
        {
            this.loadBulkMessage(this.$route.query.bulk_message_id);
            this.startPolling();
        }
        // For existing imports, the child will emit 'bulk-message-id-available' when import_meta_data loads
    },
    beforeDestroy() {
        this.stopPolling();
    },
    methods: {
        // Handle event from child when bulk message ID becomes available from import_meta_data
        onBulkMessageIdAvailable(bulk_message_id) {
            // Only load if we haven't already loaded (e.g., from query param)
            if (!this.target_bulk_message && bulk_message_id)
            {
                this.loadBulkMessage(bulk_message_id);
                this.startPolling();
            }
        },
        loadBulkMessage(bulk_message_id) {
            if (!bulk_message_id)
            {
                return;
            }
            // Store the bulk message ID for polling
            this.polling_bulk_message_id = bulk_message_id;
            this.$rc.rest({
                spinner: false,
                endpoint: 'GET: /api/v1/bulk/messages/:bulk_message_id',
                params: {
                    bulk_message_id: bulk_message_id
                },
                on_success: (data) => {
                    this.target_bulk_message = data;
                    this.target_bulk_message_name = data.label || data.name || 'Bulk Message';
                },
                on_error: () => {
                    this.target_bulk_message = null;
                    this.target_bulk_message_name = null;
                }
            });
        },
        startPolling() {
            this.stopPolling();

            // Poll every 10 seconds to check if bulk message state has changed
            this.bulk_message_poll_interval = setInterval(() => {
                if (this.polling_bulk_message_id)
                {
                    this.loadBulkMessage(this.polling_bulk_message_id);
                }
            }, 10000);
        },
        stopPolling() {
            if (this.bulk_message_poll_interval)
            {
                clearInterval(this.bulk_message_poll_interval);
                this.bulk_message_poll_interval = null;
            }
        }
    },
    computed: {
        is_remove_mode() {
            return this.$route.query.mode === 'remove';
        },
        import_execution_mode() {
            return this.is_remove_mode ? 'remove' : 'add-or-update';
        },
        page_copy() {
            if (this.is_remove_mode) {
                return {
                    pageTitle: 'Remove Bulk Message Recipients',
                    instructionsText: 'Upload an Excel file to remove matching recipients from this bulk message. Recipients are matched by phone number.',
                    uploadProgressDescription: 'Uploading and processing your removal file... please wait.',
                    executionProgressTitle: 'Removing Recipients from Bulk Message',
                    executionProgressDescription: 'Please wait while we remove matching recipients. This can take several minutes.',
                    executionStatusMessage: 'Removing recipients...',
                    completionTitle: 'Bulk Message Recipients Removal Complete!',
                    completionDescription: 'Matching recipients have been removed from the bulk message.',
                    historyRouteName: 'search-bulk-message-recipient-imports',
                };
            }
            return {
                pageTitle: 'Import Bulk Message Recipients',
                instructionsText: 'Upload an Excel file to import contacts and add them as recipients to a bulk message.',
                uploadProgressDescription: 'Uploading and processing your recipients file... please wait.',
                executionProgressTitle: 'Adding Recipients to Bulk Message',
                executionProgressDescription: 'Please wait while we import contacts and add them as recipients. This can take several minutes.',
                executionStatusMessage: 'Importing...',
                completionTitle: 'Bulk Message Recipients Import Complete!',
                completionDescription: 'Your contacts have been imported and added as recipients.',
                historyRouteName: 'search-bulk-message-recipient-imports',
            };
        },
        field_definitions() {
            // In remove mode, only phone number is needed - no optional contact data fields
            const optionalFields = this.is_remove_mode ? [] : [
                { key: 'birthdate', label: 'Birthdate', description: "Recipient's birthdate", multiple: false, required: false, hint: 'Select the column containing birthdates (supports MM/DD/YYYY or YYYY-MM-DD formats)' },
                { key: 'email', label: 'Email', description: "Recipient's email address", multiple: false, required: false, hint: 'Select the column containing email addresses' },
                { key: 'notes', label: 'Notes', description: "Recipient's notes", multiple: false, required: false, hint: 'Select the column containing notes' }
            ];
            return {
                requiredFields: [
                    { key: 'phone_number', label: 'Phone Number', description: 'Phone number(s) for the recipient - you can select multiple columns', multiple: true, required: true, hint: 'Select one or more columns containing phone numbers' },
                    { key: 'first_name', label: 'First Name', description: "Recipient's first name", multiple: false, required: true, hint: 'Select the column containing first names' },
                    { key: 'last_name', label: 'Last Name', description: "Recipient's last name", multiple: false, required: true, hint: 'Select the column containing last names' }
                ],
                optionalFields
            };
        },
        preview_config() {
            const status_column_text = this.is_remove_mode 
                ? 'Can Remove From Bulk Message?' 
                : 'Can Add To Bulk Message?';
            const status_column_hint = this.is_remove_mode
                ? 'Shows if this recipient will be removed from the bulk message'
                : 'Shows if this recipient will be added to the bulk message';

            const table_headers = [
                { text: 'Include', value: 'select', align: 'center', sortable: false, alwaysShow: true },
                { text: 'Row #', value: 'row_number', width: '60px', align: 'center', alwaysShow: true },
                { text: 'Import Status', value: 'import_status', align: 'center', alwaysShow: true, hint: 'Shows if the contact record will be created, updated, or unchanged' },
                { text: status_column_text, value: 'rc_bulk_message_recipient_status', align: 'center', alwaysShow: true, hint: status_column_hint },
                { text: 'Phone Number', value: 'rc_phone_number', align: 'left', alwaysShow: true },
                { text: 'First Name', value: 'rc_first_name' },
                { text: 'Last Name', value: 'rc_last_name' }
            ];

            // Only show email, birthdate, and notes columns in add mode
            if (!this.is_remove_mode) {
                table_headers.push(
                    { text: 'Email', value: 'rc_email' },
                    { text: 'Birthdate', value: 'rc_birthdate' },
                    { text: 'Notes', value: 'rc_notes' }
                );
            }

            if (!this.is_remove_mode) {
                table_headers.push({ text: 'Phone Valid?', value: 'rc_validation_status', hint: 'Shows if the phone number format is valid' });
            }

            table_headers.push(
                { text: 'Extra Fields', value: 'rc_extra_fields', align: 'center', hint: 'Additional custom fields from the import' },
                { text: 'System Notes', value: 'rc_system_notes' },
                { text: 'Errors', value: 'errors', alwaysShow: true }
            );

            return {
                tableHeaders: table_headers,
                customColumns: [
                    { key: 'select', type: 'custom' },
                    { key: 'rc_bulk_message_recipient_status', type: 'enum' },
                    { key: 'rc_extra_fields', type: 'custom' }
                ]
            };
        }
    }
};
</script>

<style scoped>
.bulk-message-recipients-import-upload {
    position: relative;
}
</style>
